import { PencilIcon, PlusIcon } from '@heroicons/react/20/solid';
import moment from 'moment/moment';
import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { CoproTableData } from '../../../@types/CoproTableData';
import { setCoprosFormData } from '../../../store/CoproReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { coproTypes } from '../../../utils/copros';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import CoproForm from '../../common/CoproForm';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import { SetupPage, updateSetupState } from '../../common/SetupPage';
import Spinner from '../../common/Spinner';
import Table from '../../common/Table';

const matchedKeys = ['id', 'remoteId', 'name', 'batchSize', 'type', 'validatedByAdmin'];
const headerKeys = ['id', 'N° IMMAT', 'NOM', 'NB LOTS HAB / LOCAL PRO', 'TYPE', 'VALIDE', 'REPRÉSENTANT LÉGAL', 'MANDANT', 'MODIFIER'];
const sortableColumns = {
  'N° IMMAT': 'string', NOM: 'string', ADRESSE: 'string', CP: 'string', COMMUNE: 'string', 'NB LOTS HAB / LOCAL PRO': 'number', TYPE: 'string', MANDANT: 'string', VALIDE: 'string',
};

function ActivitySetupStepCoproAdd() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const coprosFormData = useSelector((state: RootState) => state.copro.coprosFormData);
  const [data, setData] = useState<Partial<CoproTableData>[]>(null);
  const [status, setStatus] = useState('IDLE');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingCopro, setEditingCopro] = useState(null);
  const [validatedCopros, setValidatedCopros] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [isTop, setIsTop] = useState(true);
  const [isBottom, setIsBottom] = useState(false);
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const topObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTop(true);
        } else if (!entry.isIntersecting) {
          setIsTop(false);
        }
      },
      { threshold: 1 },
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsBottom(true);
        } else if (!entry.isIntersecting) {
          setIsBottom(false);
        }
      },
      { threshold: 0 },
    );

    if (topRef.current) {
      topObserver.observe(topRef.current);
    }

    if (bottomRef.current) {
      bottomObserver.observe(bottomRef.current);
    }

    return () => {
      if (topRef.current) {
        topObserver.unobserve(topRef.current);
      }
      if (bottomRef.current) {
        bottomObserver.unobserve(bottomRef.current);
      }
    };
  }, [topRef, bottomRef]);

  const openEditPopup = (copro) => {
    setEditingCopro(copro);
    setIsPopupOpen(true);
  };

  const fetchValidatedCopros = async() => {
    try {
      const response = await fetch(`/api/companies/${company.id}/validated-copros/rnics`, JWTHeader(jwt));

      if (!response.ok) {
        throw new Error('Error fetching data');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching data :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return null;
    }
  };

  const fetchData = async() => {
    setIsPopupOpen(false);
    setStatus('PENDING');
    try {
      const response = await fetch(`/api/companies/${company.id}/copros?limit=10000&filterValidatedByUser=true&includeRefusedByAdmin=true&sortField=id&sortDirection=desc`, JWTHeader(jwt));
      const result = await response.json();
      setValidatedCopros(await fetchValidatedCopros());
      dispatch(setCoprosFormData(result.results));
    } catch (error) {
      console.error('Error fetching data :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      setStatus('ERROR');
    }
  };

  const handleNextStep = async() => {
    if (status === 'PENDING' || status === 'IDLE') {
      return;
    }
    setStatus('PENDING');
    await updateSetupState(company, jwt, 'addMissingCopros', dispatch);
    setStatus('SUCCESS');
    navigate(ROUTES.ANALYZING_ADD_ADDRESSES);
  };

  const handleValidateAndGoToParc = async() => {
    if (status === 'PENDING' || status === 'IDLE') {
      return;
    }
    setStatus('PENDING');
    await updateSetupState(company, jwt, 'addMissingCopros', dispatch);
    setStatus('SUCCESS');
    navigate(ROUTES.ANALYZING_MARKET_VISION);
  };

  const renderSaveButton = () => (
    <div
      className={classNames(
        'text-right w-full md:w-auto mt-10 flex flex-col md:flex-row gap-4 justify-end',
        !isTop && !isBottom && 'fixed z-[1000] right-0 bottom-0 md:right-5 md:bottom-5 bg-white rounded',
      )}
    >
      <button
        onClick={handleValidateAndGoToParc}
        className={classNames(
          'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
          (status === 'PENDING' || status === 'IDLE') && 'opacity-80 cursor-not-allowed',
        )}
      >
        {status === 'PENDING' ? (
          <>
            <Spinner aria-label="Center-aligned spinner" size="sm" />
            <span className="ml-2">Enregistrement</span>
          </>
        ) : (
          'Enregistrer & Voir mon parc'
        )}
      </button>
      <button
        onClick={handleNextStep}
        className={classNames(
          'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
          (status === 'PENDING' || status === 'IDLE') && 'opacity-80 cursor-not-allowed',
        )}
      >
        {status === 'PENDING' ? (
          <>
            <Spinner aria-label="Center-aligned spinner" size="sm" />
            <span className="ml-2">Enregistrement</span>
          </>
        ) : (
          'Prochaine étape'
        )}
      </button>
    </div>
  );

  useEffect(() => {
    if (coprosFormData && validatedCopros) {
      const formattedData = coprosFormData.map((item) => {
        const newItem: Partial<CoproTableData> = {};

        const isSubcontracted = item.company && item.company.id !== company.id && item.company.subcontractors?.some((subcontractor) => subcontractor.id === company.id);

        // Add the actions column
        newItem.MODIFIER = (
          <div className="flex w-full items-center justify-center">
            <PencilIcon
              className={`h-5 w-5 ${isSubcontracted ? 'cursor-not-allowed text-gray-500' : 'cursor-pointer text-gray-600 hover:text-gray-800'}`}
              onClick={!isSubcontracted ? () => openEditPopup(item.id) : null}
            />
          </div>
        );

        matchedKeys.forEach((key, index) => {
          newItem[headerKeys[index]] = item[key];
        });

        newItem.TYPE = coproTypes[item.type];

        if (item.validatedByAdmin === null) {newItem.VALIDE = <div className="h-full w-full bg-amber-400 text-center text-black">en cours</div>;}
        if (item.validatedByAdmin === false) {newItem.VALIDE = <div className="h-full w-full bg-red-600 text-center text-white">refusée</div>;}
        if (item.validatedByAdmin === true) {newItem.VALIDE = <div className="h-full w-full bg-rgpd-green text-center text-white">validée</div>;}

        newItem.generalMeetingApiDate = moment(item.generalMeetingApiDate).format('YYYY-MM-DD');
        newItem.generalMeetingDate = moment(item.generalMeetingDate).format('YYYY-MM-DD');

        const matchingCopro = validatedCopros.find((vCopro) => vCopro.numeroDimmatriculation === item.remoteId);

        newItem.MANDANT = isSubcontracted
          ? (
            <div className="text-xs">
              {item.company.name}
            </div>
          ) : '';

        if (matchingCopro) {
          const isMatch = matchingCopro.siretRepresentantLegalSiExiste?.startsWith(company.siren);
          newItem['REPRÉSENTANT LÉGAL'] = (
            <div className={isMatch ? 'h-full w-full bg-rgpd-green text-xs text-rgpd-white' : 'h-full w-full bg-red-600 text-xs text-rgpd-white'}>
              {matchingCopro.identificationDuRepresentantLegalRaisonSocialeEtLeNume}
            </div>
          );
        }

        if (item.addresses.length === 0) {
          newItem.ADRESSE = '';
          newItem.CP = '';
          newItem.COMMUNE = '';
          return newItem;
        }
        newItem.ADRESSE = item.addresses[0].address;
        newItem.CP = item.addresses[0].postalCode;
        newItem.COMMUNE = item.addresses[0].city;

        return newItem;
      });

      setData(formattedData);
      setStatus('SUCCESS');
    }
  }, [coprosFormData, validatedCopros]);

  const openAddPopup = () => {
    setEditingCopro(null);
    setIsPopupOpen(true);
  };

  const handleExport = async() => {
    if (company && company.id && jwt) {
      setExportLoading(true);
      fetch(`/api/companies/${company.id}/copros/export/csv`, JWTHeader(jwt))
        .then((response) => response.blob())
        .then((blobby) => {
          const objectUrl = window.URL.createObjectURL(blobby);
          const anchor = document.createElement('a');
          document.body.appendChild(anchor);
          anchor.href = objectUrl;
          anchor.download = 'declarations-juridiques-coproprietes-export.csv';
          anchor.click();

          window.URL.revokeObjectURL(objectUrl);
        })
        .catch((error) => {
          console.error('Error generating csv :', error);
          showToast('error', 'Une erreur est survenue lors de la génération du fichier.');
        });
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (jwt && company && company.id && status !== 'PENDING') {
      fetchData();
    }
  }, [jwt, company]);

  return (
    <SetupPage
      step={'addMissingCopros'}
      title="AJOUTER LES COPROPRIÉTÉS MANQUANTES AINSI QUE LES ASL, AFUL ET LES IMMEUBLES TERTIAIRES QUE VOUS GÉREZ"
      children={(
        <>
          <div ref={topRef} style={{ height: '1px' }} />
          
          <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex flex-col items-start justify-between gap-8 lg:flex-row">
              <div className="flex-1">
                <p className="mb-6 text-lg leading-relaxed font-medium text-gray-800">
                  À CE JOUR VOUS AVEZ VALIDÉ
                  <span className="mx-1 font-bold text-rgpd-blue-light">
                    {data && data.length }
                    {!data && <Skeleton height={15} width={30} inline />}
                    {' '}
                    IMMEUBLES DANS NOTRE INTERFACE
                  </span>
                  DONT
                  <span className="mx-1 font-bold text-rgpd-blue-light">
                    {data && data.length > 0 && data.filter((item) => item['N° IMMAT']).length}
                    {!data && <Skeleton height={15} width={30} inline />}
                    {' '}
                    COPROPRIÉTÉS HORS ASL, AFUL ET IMMEUBLES TERTIAIRES
                  </span>
                  SONT RATTACHÉES À VOTRE NOM DANS LE REGISTRE DES COPROPRIÉTÉS
                </p>
                
              </div>

            </div>

            <div className="mt-8 border-t border-gray-100 pt-6">
              <h2 className="flex flex-col items-start gap-3 text-xl font-bold text-gray-800 md:flex-row md:items-center">
                <span className="rounded bg-rgpd-blue-light px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">ÉTAPE N°2 :</span>
                <span>AJOUTER LES COPROPRIÉTÉS MANQUANTES AINSI QUE LES ASL, AFUL ET LES IMMEUBLES TERTIAIRES QUE VOUS GÉREZ (une vérification sera effectuée ensuite par nos services)</span>
              </h2>
            </div>
          </div>

          {status === 'PENDING' && (
            <p className="mx-10 my-32 text-center">
              <Spinner aria-label="Center-aligned spinner" size="xl" />
            </p>
          )}
          {status === 'SUCCESS' && data && (
            <>
              <h2 className="mt-16 mb-8 px-4 text-center text-xl font-semibold text-rgpd-blue-light">LISTE DES IMMEUBLES AJOUTÉS AVEC LEUR STATUT DE VALIDATION PAR LES ÉQUIPES DE DÉCLARATIONS JURIDIQUES</h2>
              <div className="mb-8 flex w-full flex-col items-start justify-between gap-6 rounded-lg border border-gray-100 bg-white p-4 shadow-sm md:flex-row md:items-center">
                <div className="flex flex-col items-start">
                  <div className="mb-3 flex items-center">
                    <div className="font-semibold text-gray-700">Légende pour la colonne "REPRÉSENTANT LÉGAL" :</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                      <div className="h-4 w-4 shrink-0 rounded-sm bg-rgpd-green" />
                      <div className="text-sm text-gray-600">Votre entreprise est le représentant légal dans le dernier fichier RNIC</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-4 w-4 shrink-0 rounded-sm bg-red-600" />
                      <div className="text-sm text-gray-600">Une autre entreprise est le représentant légal dans le dernier fichier RNIC</div>
                    </div>
                  </div>
                </div>
                <div className="w-full md:w-auto">
                  <button
                    className="w-full rounded-lg border border-rgpd-blue-light px-4 py-2 text-sm font-medium text-rgpd-blue-light transition-all hover:bg-rgpd-blue-light hover:text-white md:w-auto"
                    onClick={handleExport}
                  >
                    {exportLoading && <Spinner aria-label="Center-aligned spinner" size="sm" className="mx-4 my-0" />}
                    {!exportLoading && 'Exporter en CSV'}
                  </button>
                </div>
              </div>
              <div className="my-6 flex w-full justify-center">
                <button 
                  onClick={openAddPopup} 
                  className="hover:bg-opacity-90 flex items-center justify-center rounded-lg bg-rgpd-blue-dark px-6 py-3 font-semibold text-white shadow-md transition-all"
                >
                  <PlusIcon className="mr-2 h-5 w-5" />
                  Ajouter une nouvelle copropriété
                </button>
              </div>
              <Table
                isSearchable
                sortableColumns={sortableColumns}
                data={data}
                headers={['MODIFIER', 'N° IMMAT', 'NOM', 'ADRESSE', 'CP', 'COMMUNE', 'NB LOTS HAB / LOCAL PRO', 'TYPE', 'REPRÉSENTANT LÉGAL', 'MANDANT', 'VALIDE']}
                tableClass="text-sm"
                stickyHeader
              />
              {data && data.length === 0 && (
                <div className="flex w-full place-content-center">
                  <Badge
                    mode="warning"
                    text="Aucune copropriété trouvée"
                    className="mt-6 mb-4"
                  />
                </div>
              )}
              <Popup
                isOpen={isPopupOpen}
                onClose={() => {
                  setIsPopupOpen(false);
                }}
                title={editingCopro ? 'Modifier la copropriété' : 'Ajouter une nouvelle copropriété'}
                additionalClass="h-4/6 w-6/12"
                children={(
                  <CoproForm
                    initialDataId={editingCopro}
                    setIsPopupOpen={setIsPopupOpen}
                    companyId={company.id}
                  />
                )}
              />
            </>
          )}
          {status === 'ERROR' && (
            <div className="flex w-full place-content-center">
              <Badge
                mode="error"
                text="Erreur lors du chargement des données"
              />
            </div>
          )}
          
          <div ref={bottomRef} style={{ height: '1px' }} />
          {renderSaveButton()}
        </>
      )}
    />
  );
}

export default ActivitySetupStepCoproAdd;